/**
 * Production-Ready Logging System
 * Provides structured logging with different levels and environment-based output
 */

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  FATAL = 4,
}

interface LogEntry {
  timestamp: string;
  level: LogLevel;
  message: string;
  context?: string;
  metadata?: Record<string, unknown>;
  userId?: string;
  sessionId?: string;
  url?: string;
}

class Logger {
  private level: LogLevel;
  private isDevelopment: boolean;
  private sessionId: string;

  constructor() {
    this.isDevelopment = import.meta.env.DEV;
    this.level = this.isDevelopment ? LogLevel.DEBUG : LogLevel.INFO;
    this.sessionId = this.generateSessionId();
  }

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private createLogEntry(
    level: LogLevel,
    message: string,
    context?: string,
    metadata?: Record<string, unknown>,
  ): LogEntry {
    return {
      timestamp: new Date().toISOString(),
      level,
      message,
      context,
      metadata,
      sessionId: this.sessionId,
      url: window?.location?.href,
      userId: this.getCurrentUserId(),
    };
  }

  private getCurrentUserId(): string | undefined {
    // TODO: Get from auth context when implemented
    return undefined;
  }

  private shouldLog(level: LogLevel): boolean {
    return level >= this.level;
  }

  private formatForConsole(entry: LogEntry): void {
    const prefix = `[${entry.timestamp}] [${LogLevel[entry.level]}]`;
    const contextStr = entry.context ? ` [${entry.context}]` : "";
    const message = `${prefix}${contextStr} ${entry.message}`;

    switch (entry.level) {
      case LogLevel.DEBUG:
        console.warn(message, entry.metadata);
        break;
      case LogLevel.INFO:
        console.warn(message, entry.metadata);
        break;
      case LogLevel.WARN:
        console.warn(message, entry.metadata);
        break;
      case LogLevel.ERROR:
      case LogLevel.FATAL:
        console.error(message, entry.metadata);
        break;
    }
  }

  private async sendToMonitoring(entry: LogEntry): Promise<void> {
    if (
      !this.isDevelopment &&
      (entry.level === LogLevel.ERROR || entry.level === LogLevel.FATAL)
    ) {
      try {
        // Dynamic import to avoid loading Sentry if not needed
        const Sentry = await import("@sentry/react");

        if (entry.metadata?.error instanceof Error) {
          Sentry.captureException(entry.metadata.error, {
            extra: {
              context: entry.context,
              ...entry.metadata,
              sessionId: entry.sessionId,
              url: entry.url,
            },
            level: entry.level === LogLevel.FATAL ? "fatal" : "error",
          });
        } else {
          Sentry.captureMessage(entry.message, {
            level: entry.level === LogLevel.FATAL ? "fatal" : "error",
            extra: {
              context: entry.context,
              ...entry.metadata,
              sessionId: entry.sessionId,
              url: entry.url,
            },
          });
        }
      } catch (error) {
        // Fallback to console if Sentry fails
        console.error("Failed to send error to Sentry:", error);
      }
    }
  }

  public debug(
    message: string,
    context?: string,
    metadata?: Record<string, unknown>,
  ): void {
    if (!this.shouldLog(LogLevel.DEBUG)) {
      return;
    }

    const entry = this.createLogEntry(
      LogLevel.DEBUG,
      message,
      context,
      metadata,
    );
    this.formatForConsole(entry);
  }

  public info(
    message: string,
    context?: string,
    metadata?: Record<string, unknown>,
  ): void {
    if (!this.shouldLog(LogLevel.INFO)) {
      return;
    }

    const entry = this.createLogEntry(
      LogLevel.INFO,
      message,
      context,
      metadata,
    );
    this.formatForConsole(entry);
  }

  public warn(
    message: string,
    context?: string,
    metadata?: Record<string, unknown>,
  ): void {
    if (!this.shouldLog(LogLevel.WARN)) {
      return;
    }

    const entry = this.createLogEntry(
      LogLevel.WARN,
      message,
      context,
      metadata,
    );
    this.formatForConsole(entry);
    this.sendToMonitoring(entry);
  }

  public error(
    message: string,
    context?: string,
    metadata?: Record<string, unknown>,
  ): void {
    if (!this.shouldLog(LogLevel.ERROR)) {
      return;
    }

    const entry = this.createLogEntry(
      LogLevel.ERROR,
      message,
      context,
      metadata,
    );
    this.formatForConsole(entry);
    this.sendToMonitoring(entry);
  }

  public fatal(
    message: string,
    context?: string,
    metadata?: Record<string, unknown>,
  ): void {
    const entry = this.createLogEntry(
      LogLevel.FATAL,
      message,
      context,
      metadata,
    );
    this.formatForConsole(entry);
    this.sendToMonitoring(entry);
  }

  // Utility methods for common patterns
  public apiRequest(
    method: string,
    url: string,
    metadata?: Record<string, unknown>,
  ): void {
    this.debug(`API Request: ${method.toUpperCase()} ${url}`, "API", metadata);
  }

  public apiError(method: string, url: string, error: unknown): void {
    this.error(`API Error: ${method.toUpperCase()} ${url}`, "API", {
      error: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined,
    });
  }

  public userAction(action: string, metadata?: Record<string, unknown>): void {
    this.info(`User Action: ${action}`, "USER", metadata);
  }

  public componentError(component: string, error: unknown): void {
    this.error(`Component Error in ${component}`, "REACT", {
      error: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined,
    });
  }

  public performance(
    operation: string,
    duration: number,
    metadata?: Record<string, unknown>,
  ): void {
    this.info(`Performance: ${operation} took ${duration}ms`, "PERF", metadata);
  }
}

// Export singleton instance
export const logger = new Logger();

// Export utility functions for common patterns
export const logApiRequest = logger.apiRequest.bind(logger);
export const logApiError = logger.apiError.bind(logger);
export const logUserAction = logger.userAction.bind(logger);
export const logComponentError = logger.componentError.bind(logger);
export const logPerformance = logger.performance.bind(logger);
